import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../Components/Form/withForm';
import FormControl from '../../../../../Components/Form/FormControl';
import TimePicker from '../../../../../Components/Base/TimePicker/TimePicker';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import moment, { Moment } from 'moment-timezone';
import { getDate } from '../../../../../Helpers/date.helper';
import {
	NCZIReferenceSelectItem,
	NCZIReferenceValidator,
} from '../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import { WritePatientDPN } from '../../../Commands/WritePatientDPN';
import InputWrapper from '../../../../../Components/Form/components/InputWrapper/InputWrapper';
import TextAreaField from '../../../../../Components/Form/components/TextAreaField';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import FormContainer from '../../../../../Components/Form/FormContainer';
import InputField from '../../../../../Components/Form/components/InputField';
import NCZIReferenceSelectField from '../../ReferenceSelect/NCZIReferenceSelectField';
import { NCZI_OID } from '../../../Enums/ncziOid.enum';
import NCZIReferenceSearchSelectField from '../../ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import RemoveButton from '../../../../../Components/Button/RemoveButton';
import FormMultiplier from '../../../../../Components/Form/FormMultiplier';
import FormTitle from '../../../../../Components/Form/FormTitle';
import { yupMoment } from '../../../../../Components/Utils/Validation';
import { useLoadProfile } from '../../../../../Components/PatientProfile/PatientProfileDataLoader';
import { usePatientTreatmentContext } from '../../../../Patient/Components/PatientTreatment/ContextProvider';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import SelectField from '../../../../../Components/Form/components/SelectField';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import useTranslation from '../../../../../Hooks/useTranslation';

//@ts-ignore
import styles from './ElectronicDPNCreateForm.module.scss';
import Text from '../../../../../Components/Base/Text/Text';
import InsuranceExemptionListModal from '../InsuranceExemptionList/InsuranceExemptionListModal';
import { Theme } from '../../../../../Enums/Theme.enum';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { NCZIScheme } from '../../../Interfaces/ncziTypes';

export interface ElectronicDPNCreateFormValues {
	diagnosis: {
		code: NCZIReferenceSelectItem;
		validFrom: Moment | undefined;
	};
	incapableFrom: Moment;
	capableFrom: Moment | undefined;
	incapableReason: {
		reason: NCZIReferenceSelectItem;
		alcoholConsumption: SelectValue;
		validFrom: Moment | undefined;
	};
	incapableDuration: {
		anticipatedTerminationDate: Moment;
		note: string | undefined;
	};
	// issueDate: Moment;
	note: string;
	outings: {
		dateFrom: Moment;
		timeFrom: Moment;
		timeTo: Moment;
	}[];
	phone: string | undefined;
	placeOfStay: {
		note: string | undefined;
		floor: string | undefined;
		apartmentNumber: string | undefined;
		address: {
			city: string;
			country: string;
			descriptiveNumber: string;
			guideNumber: string;
			postCode: string;
			street: string;
		};
	};
	treatmentAbroad: boolean;
}

interface Props {
	onClose?: (needReload: boolean) => void;
	patientIdentifier: string;
	electronicConfirmation?: any;
}

const ElectronicDPNCreateForm: React.FC<Props> = props => {
	const { onClose, patientIdentifier, electronicConfirmation } = props;
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => onClose?.(true),
		});
	const { patientId } = usePatientTreatmentContext();
	const { t } = useTranslation();
	const { user } = useLoadProfile({
		userId: patientId,
		loadVerifyInformation: false,
		loadUserRelations: false,
		loadHealthCard: true,
	});
	const [incapableFromValue, setIncapableFromValue] = useState<
		Moment | undefined
	>(undefined);
	const [capableFromValue, setCapableFromValue] = useState<Moment | undefined>(
		undefined,
	);
	const [anticipatedTerminationDateValue, setAnticipatedTerminationDateValue] =
		useState<Moment | undefined>(undefined);
	const [allowContinue, setAllowContinue] = useState<boolean>(true);
	const [usingContinue, setUsingContinue] = useState<boolean>(false);
	const [insuranceExemptionListModalOpen, setInsuranceExemptionListModalOpen] =
		useState<boolean>(false);
	const [exemptionedItems, setExemptionedItems] = useState<string[]>([]);

	const alcoholOptions = [
		{
			value: 'null',
			label: t('base.shared.notSpecified') || '',
		},
		{
			value: 'false',
			label: t('base.no') || '',
		},
		{
			value: 'true',
			label: t('base.yes') || '',
		},
	];

	const handleInsuranceExemption = (values: string[]) => {
		setExemptionedItems(values);
		setInsuranceExemptionListModalOpen(false);
	};

	const validationSchema = yup.object({
		diagnosis: yup.object({
			code: NCZIReferenceValidator('nczi.electronicDPN.create.diagnosis.code'),
			validFrom: yupMoment(
				'nczi.electronicDPN.create.diagnosis.validFrom',
				true,
			).test(
				'diagnosis-from-incapableFrom',
				'nczi.electronicDPN.create.validation.minFromIncapableFrom',
				value => {
					if (!value) {
						return true;
					}
					const differenceInDays = value.diff(incapableFromValue, 'days');
					return differenceInDays >= 0;
				},
			),
		}),
		incapableDuration: yup.object({
			anticipatedTerminationDate: yupMoment(
				'nczi.electronicDPN.create.incapableDuration.anticipatedTerminationDate',
			)
				.test(
					'anticipatedTerminationDate-from-incapableFrom',
					'nczi.electronicDPN.create.validation.minFromIncapableFrom',
					value => {
						if (!value) {
							return true;
						}
						const differenceInDays = value.diff(incapableFromValue, 'days');
						return differenceInDays >= 0;
					},
				)
				.test(
					'anticipatedTerminationDate-after-capableFrom',
					'nczi.electronicDPN.create.validation.maxBeforeCapableFrom',
					value => {
						if (!value || !capableFromValue) {
							return true;
						}
						const differenceInDays = capableFromValue.diff(value, 'days');
						return differenceInDays >= 1;
					},
				),
			note: yup.string().max(2000, 'nczi.validation.max2000'),
		}),
		incapableFrom: yupMoment('nczi.electronicDPN.create.incapableFrom').when(
			'treatmentAbroad',
			{
				is: false,
				then: yup
					.mixed()
					.test(
						'moment-max-3days-before',
						'nczi.electronicDPN.create.validation.incapableFromMax3days',
						value => {
							const inputDate = moment().subtract(3, 'days');
							const differenceInDays = value.diff(inputDate, 'days');
							return differenceInDays >= 0;
						},
					)
					.test(
						'moment-max-1days-after',
						'nczi.electronicDPN.create.validation.incapableFromMax3days',
						value => {
							const inputDate = moment();
							const differenceInDays = inputDate.diff(value, 'days');
							return differenceInDays >= 0;
						},
					),
			},
		),
		incapableReason: yup.object({
			reason: NCZIReferenceValidator(
				'nczi.electronicDPN.create.incapableReason.reason',
			),
			alcoholConsumption: yup
				.object({
					label: yup.string(),
					value: yup.string(),
				})
				.test(
					'alcohol-required',
					'nczi.electronicDPN.create.incapableReason.alcoholConsumption',
					(value, context) => {
						const isAlcoholRequired =
							['02089917401', '02089917427'].indexOf(
								context.parent.reason.value.code,
							) !== -1;
						return (
							!isAlcoholRequired ||
							(isAlcoholRequired && value.value !== 'null')
						);
					},
				),
			validFrom: yupMoment(
				'nczi.electronicDPN.create.incapableReason.validFrom',
				true,
			).test(
				'reason-from-incapableFrom',
				'nczi.electronicDPN.create.validation.minFromIncapableFrom',
				value => {
					if (!value) {
						return true;
					}
					const differenceInDays = value.diff(incapableFromValue, 'days');
					return differenceInDays >= 0;
				},
			),
		}),
		capableFrom: yupMoment('nczi.electronicDPN.create.capableFrom', true).test(
			'moment-not-older',
			'nczi.electronicDPN.create.validation.capableFromNotOlder',
			value => {
				if (!value) {
					return true;
				}
				const inputDate = moment();
				const differenceInDays = value.diff(inputDate, 'days');
				return differenceInDays >= 0;
			},
		),
		// issueDate: yupMoment('nczi.electronicDPN.create.issueDate', true),
		note: yup.string().max(2000, 'nczi.validation.max2000'),
		outings: yup
			.array()
			.of(
				yup.object({
					dateFrom: yupMoment(
						'nczi.electronicDPN.create.outings.dateFrom',
					).test(
						'outing-from-incapableFrom',
						'nczi.electronicDPN.create.validation.minFromIncapableFrom',
						value => {
							if (!value) {
								return true;
							}
							const differenceInDays = value.diff(incapableFromValue, 'days');
							return differenceInDays >= 0;
						},
					),
					timeFrom: yupMoment('nczi.electronicDPN.create.outings.timeFrom'),
					timeTo: yupMoment('nczi.electronicDPN.create.outings.timeTo'),
				}),
			)
			.nullable(),
		phone: yup.string(),
		placeOfStay: yup.object({
			note: yup.string().max(2000, 'nczi.validation.max2000'),
			floor: yup.string(),
			apartmentNumber: yup.string(),
			address: yup.object({
				city: yup
					.string()
					.required('nczi.electronicDPN.create.placeOfStay.address.city'),
				country: yup
					.string()
					.required('nczi.electronicDPN.create.placeOfStay.address.country'),
				descriptiveNumber: yup
					.string()
					.required(
						'nczi.electronicDPN.create.placeOfStay.address.descriptiveNumber',
					),
				guideNumber: yup.string(),
				postCode: yup
					.string()
					.required('nczi.electronicDPN.create.placeOfStay.address.postCode'),
				street: yup
					.string()
					.required('nczi.electronicDPN.create.placeOfStay.address.street'),
			}),
		}),
		treatmentAbroad: yup.boolean(),
	});

	const formMethods = useForm<ElectronicDPNCreateFormValues>({
		defaultValues: {},
		resolver: yupResolver(validationSchema),
	});

	const incapableFromWatcher = formMethods.watch('incapableFrom');
	const capableFromWatcher = formMethods.watch('capableFrom');
	const anticipatedTerminationDateWatcher = formMethods.watch(
		'incapableDuration.anticipatedTerminationDate',
	);

	useEffect(() => {
		setIncapableFromValue(incapableFromWatcher);
		setCapableFromValue(capableFromWatcher);
		setAnticipatedTerminationDateValue(anticipatedTerminationDateWatcher);
	}, [
		incapableFromWatcher,
		capableFromWatcher,
		anticipatedTerminationDateWatcher,
	]);

	const handleSubmit = async (values: ElectronicDPNCreateFormValues) => {
		// let changeDate = moment();
		// if(changeDate.diff(values.incapableFrom, 'days') < 0) {
		// 	changeDate = values.incapableFrom;
		// }

		let alcoholConsumption = undefined;
		if (values.incapableReason.alcoholConsumption.value !== 'null') {
			alcoholConsumption =
				values.incapableReason.alcoholConsumption.value === 'true';
		}

		const finalValues = {
			createdAt: moment().format(),
			versionStatus: 'VER01',
			sensitivity: 3,
			uncertaintyExpressed: false,
			incapableFrom: usingContinue
				? electronicConfirmation.capableFrom
				: values.incapableFrom.format('YYYY-MM-DD'),
			capableFrom: values.capableFrom?.format('YYYY-MM-DD') || undefined,
			issueDate: moment().format('YYYY-MM-DD'),
			treatmentAbroad: usingContinue
				? electronicConfirmation.treatmentAbroad
				: values.treatmentAbroad,
			note: values.note || undefined,
			outings:
				values.outings.length > 0
					? {
							allowedOutings: values.outings.map(outing => ({
								dateFrom: outing.dateFrom.format('YYYY-MM-DD'),
								timeFrom: outing.timeFrom.format('HH:mm'),
								timeTo: outing.timeTo.format('HH:mm'),
							})),
					  }
					: undefined,
			placeOfStay: {
				note: values.placeOfStay.note || undefined,
				floor: values.placeOfStay.floor || undefined,
				apartmentNumber: values.placeOfStay.apartmentNumber || undefined,
				address: {
					street: values.placeOfStay.address.street,
					descriptiveNumber: values.placeOfStay.address.descriptiveNumber,
					guideNumber: values.placeOfStay.address.guideNumber,
					country: values.placeOfStay.address.country,
					city: values.placeOfStay.address.city,
					postCode: values.placeOfStay.address.postCode,
				},
			},
			incapableReason: {
				reason: values.incapableReason.reason.value,
				alcoholConsumption,
				validFrom:
					values.incapableReason.validFrom?.format('YYYY-MM-DD') || undefined,
			},
			incapableDuration: {
				anticipatedTerminationDate:
					values.incapableDuration.anticipatedTerminationDate.format(
						'YYYY-MM-DD',
					),
				note: values.incapableDuration.note || undefined,
			},
			diagnosis: {
				code: values.diagnosis.code.value,
				validFrom: values.diagnosis.validFrom?.format('YYYY-MM-DD'),
			},
			changeDate: moment().format('YYYY-MM-DD'),
			phone: values.phone || undefined,
			previousVersion: undefined,
			insuranceExemption:
				exemptionedItems.length > 0
					? exemptionedItems.map(code => {
							return {
								code,
								scheme: {
									oid: '1.3.158.00165387.100.40.85',
								},
								valid: undefined,
							};
					  })
					: undefined,
		};

		const writeCommand = WritePatientDPN({
			patientCode: patientIdentifier,
			confirmation: finalValues,
		});

		await processCommand(writeCommand);
	};

	useEffect(() => {
		formMethods.setValue(
			'incapableReason.alcoholConsumption',
			alcoholOptions[0],
		);

		const setDefaultNew = () => {
			const now = getDate();
			formMethods.setValue('incapableFrom', now);

			const address = user?.profile?.address;
			if (address) {
				formMethods.setValue('placeOfStay.address.street', address.street);
				formMethods.setValue('placeOfStay.address.city', address.city);
				formMethods.setValue(
					'placeOfStay.address.descriptiveNumber',
					address.descriptiveNumber,
				);
				formMethods.setValue(
					'placeOfStay.address.guideNumber',
					address.guideNumber,
				);
				formMethods.setValue('placeOfStay.address.postCode', address.postCode);
				formMethods.setValue('placeOfStay.address.country', 'Slovensko');
			}
			if (user?.phoneNumber) {
				formMethods.setValue('phone', user.phoneNumber);
			}
		};

		if (electronicConfirmation) {
			const continueCapableFrom = moment(electronicConfirmation.capableFrom);
			const differenceInDays = continueCapableFrom.diff(moment(), 'days');
			if (differenceInDays > 1 || differenceInDays < -3) {
				setAllowContinue(false);
				setDefaultNew();
			} else {
				setUsingContinue(true);

				formMethods.setValue('incapableFrom', continueCapableFrom);
				formMethods.setValue(
					'placeOfStay.apartmentNumber',
					electronicConfirmation.placeOfStay.apartmentNumber || '',
				);
				formMethods.setValue(
					'placeOfStay.floor',
					electronicConfirmation.placeOfStay.floor || '',
				);
				formMethods.setValue(
					'placeOfStay.note',
					electronicConfirmation.placeOfStay.note || '',
				);
				formMethods.setValue(
					'placeOfStay.address.street',
					electronicConfirmation.placeOfStay.address.street,
				);
				formMethods.setValue(
					'placeOfStay.address.city',
					electronicConfirmation.placeOfStay.address.city,
				);
				formMethods.setValue(
					'placeOfStay.address.descriptiveNumber',
					electronicConfirmation.placeOfStay.address.descriptiveNumber,
				);
				formMethods.setValue(
					'placeOfStay.address.guideNumber',
					electronicConfirmation.placeOfStay.address.guideNumber,
				);
				formMethods.setValue(
					'placeOfStay.address.postCode',
					electronicConfirmation.placeOfStay.address.postCode,
				);
				formMethods.setValue(
					'placeOfStay.address.country',
					electronicConfirmation.placeOfStay.address.country,
				);
				formMethods.setValue('phone', electronicConfirmation.phone || '');
				formMethods.setValue('note', electronicConfirmation.note || '');

				let alcoholConsumption = alcoholOptions[0];
				if (
					electronicConfirmation.incapableReason?.alcoholConsumption !== null
				) {
					alcoholConsumption = electronicConfirmation.incapableReason
						?.alcoholConsumption
						? alcoholOptions[2]
						: alcoholOptions[1];
				}

				formMethods.setValue(
					'treatmentAbroad',
					electronicConfirmation.treatmentAbroad,
				);
				formMethods.setValue(
					'incapableReason.alcoholConsumption',
					alcoholConsumption,
				);
				if (electronicConfirmation.incapableReason?.reason) {
					formMethods.setValue('incapableReason.reason', {
						label: electronicConfirmation.incapableReason.reason.displayName,
						value: electronicConfirmation.incapableReason.reason,
					});
				}
				formMethods.setValue(
					'incapableDuration.note',
					electronicConfirmation.incapableDuration?.note || '',
				);
				if (electronicConfirmation.diagnosis?.code) {
					formMethods.setValue('diagnosis.code', {
						label: electronicConfirmation.diagnosis.code.displayName,
						value: electronicConfirmation.diagnosis.code,
					});
				}
				if (electronicConfirmation.outings.allowedOutings) {
					const outings: {
						dateFrom: Moment;
						timeFrom: Moment;
						timeTo: Moment;
					}[] = [];
					for (const outing of electronicConfirmation.outings.allowedOutings) {
						outings.push({
							dateFrom: continueCapableFrom,
							timeFrom: moment(outing.timeFrom, 'HH:mm'),
							timeTo: moment(outing.timeTo, 'HH:mm'),
						});
					}
					formMethods.setValue('outings', outings);
				}
			}
		} else {
			setDefaultNew();
		}
	}, [user, electronicConfirmation]);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	return (
		<div>
			{electronicConfirmation && !allowContinue && (
				<div className={styles.warning}>
					<Text tx={'nczi.electronicDPN.create.cannotContinueDueDays'} />
				</div>
			)}
			<FormWrapper formMethods={formMethods}>
				<div className={'row'}>
					<div className={'col-6'}>
						<FormControl
							label='nczi.electronicDPN.create.incapableFrom'
							name='incapableFrom'
							isRequired>
							<DatePicker disabled={usingContinue} />
						</FormControl>
					</div>
					<div className={'col-6'}>
						<FormControl
							label='nczi.electronicDPN.create.capableFrom'
							name='capableFrom'
							isRequired>
							<DatePicker />
						</FormControl>
					</div>
				</div>
				<InputWrapper>
					<TextAreaField
						label='nczi.electronicDPN.create.note'
						name='note'
						isRequired={false}
						charactersLimit={2000}
					/>
				</InputWrapper>
				<InputWrapper>
					<CheckboxField
						tx='nczi.electronicDPN.create.treatmentAbroad'
						name='treatmentAbroad'
						isRequired
						isDisabled={usingContinue}
						firstUpper
					/>
				</InputWrapper>
				<FormTitle
					tx='nczi.electronicDPN.create.incapableReason.title'
					type='default'
				/>
				<FormContainer name={'incapableReason'} settings={{ showLine: true }}>
					<InputWrapper>
						<NCZIReferenceSelectField
							label='nczi.electronicDPN.create.incapableReason.reason'
							name='reason'
							oid={NCZI_OID.DPN_REASON}
							isRequired
						/>
					</InputWrapper>
					<SelectField
						label='nczi.electronicDPN.create.incapableReason.alcoholConsumption'
						name='alcoholConsumption'
						options={alcoholOptions}
						isRequired
					/>
					{/*<CheckboxField*/}
					{/*	tx='nczi.electronicDPN.create.incapableReason.alcoholConsumption'*/}
					{/*	name='alcoholConsumption'*/}
					{/*	isRequired*/}
					{/*	firstUpper*/}
					{/*/>*/}
					<FormControl
						label='nczi.electronicDPN.create.incapableReason.validFrom'
						name='validFrom'
						isRequired>
						<DatePicker />
					</FormControl>
				</FormContainer>
				<FormTitle
					tx='nczi.electronicDPN.create.incapableDuration.title'
					type='default'
				/>
				<FormContainer name={'incapableDuration'} settings={{ showLine: true }}>
					<FormControl
						label='nczi.electronicDPN.create.incapableDuration.anticipatedTerminationDate'
						name='anticipatedTerminationDate'
						isRequired>
						<DatePicker />
					</FormControl>
					<InputWrapper>
						<TextAreaField
							label='nczi.electronicDPN.create.incapableDuration.note'
							name='note'
							isRequired={false}
							charactersLimit={2000}
						/>
					</InputWrapper>
				</FormContainer>
				<FormTitle
					tx='nczi.electronicDPN.create.diagnosis.title'
					type='default'
				/>
				<FormContainer name={'diagnosis'} settings={{ showLine: true }}>
					<InputWrapper>
						<NCZIReferenceSearchSelectField
							label='nczi.electronicDPN.create.diagnosis.code'
							name='code'
							oid={NCZI_OID.DIAGNOSIS}
							showCode
							isRequired
						/>
					</InputWrapper>
					<FormControl
						label='nczi.electronicDPN.create.diagnosis.validFrom'
						name='validFrom'
						isRequired>
						<DatePicker />
					</FormControl>
				</FormContainer>
				<FormTitle
					tx='nczi.electronicDPN.create.placeOfStay.title'
					type='default'
				/>
				<FormContainer name={'placeOfStay'} settings={{ showLine: true }}>
					<InputWrapper>
						<TextAreaField
							label='nczi.electronicDPN.create.placeOfStay.note'
							name='note'
							isRequired={false}
							charactersLimit={2000}
						/>
					</InputWrapper>
					<div className={'row'}>
						<div className={'col-6'}>
							<InputWrapper>
								<InputField
									label='nczi.electronicDPN.create.placeOfStay.floor'
									name='floor'
									isRequired={false}
								/>
							</InputWrapper>
						</div>
						<div className={'col-6'}>
							<InputWrapper>
								<InputField
									label='nczi.electronicDPN.create.placeOfStay.apartmentNumber'
									name='apartmentNumber'
									isRequired={false}
								/>
							</InputWrapper>
						</div>
					</div>
					<FormTitle
						tx='nczi.electronicDPN.create.placeOfStay.address.title'
						type='default'
					/>
					<FormContainer name={'address'} settings={{ showLine: true }}>
						<InputWrapper>
							<InputField
								label='nczi.electronicDPN.create.placeOfStay.address.street'
								name='street'
								isRequired
							/>
						</InputWrapper>
						<div className={'row'}>
							<div className={'col-6'}>
								<InputWrapper>
									<InputField
										label='nczi.electronicDPN.create.placeOfStay.address.descriptiveNumber'
										name='descriptiveNumber'
										isRequired
									/>
								</InputWrapper>
							</div>
							<div className={'col-6'}>
								<InputWrapper>
									<InputField
										label='nczi.electronicDPN.create.placeOfStay.address.guideNumber'
										name='guideNumber'
										isRequired={false}
									/>
								</InputWrapper>
							</div>
						</div>
						<InputWrapper>
							<InputField
								label='nczi.electronicDPN.create.placeOfStay.address.city'
								name='city'
								isRequired
							/>
						</InputWrapper>
						<InputWrapper>
							<InputField
								label='nczi.electronicDPN.create.placeOfStay.address.postCode'
								name='postCode'
								isRequired
							/>
						</InputWrapper>
						<InputWrapper>
							<InputField
								label='nczi.electronicDPN.create.placeOfStay.address.country'
								name='country'
								isRequired
							/>
						</InputWrapper>
					</FormContainer>
				</FormContainer>
				<InputWrapper>
					<InputField
						label='nczi.electronicDPN.create.phone'
						name='phone'
						isRequired={false}
					/>
				</InputWrapper>
				<FormTitle
					tx='nczi.electronicDPN.create.outings.title'
					type='default'
				/>
				<FormMultiplier
					name='outings'
					addButtonTitle={'nczi.electronicDPN.create.outings.add'}>
					{({ remove, index, itemsCount }) => (
						<FormContainer settings={{ showLine: true }}>
							<div className={'row'}>
								<div className={'col-4'}>
									<FormControl
										label='nczi.electronicDPN.create.outings.dateFrom'
										name='dateFrom'
										isRequired>
										<DatePicker />
									</FormControl>
								</div>
								<div className={'col-3'}>
									<FormControl
										label='nczi.electronicDPN.create.outings.timeFrom'
										name='timeFrom'
										isRequired>
										<TimePicker />
									</FormControl>
								</div>
								<div className={'col-3'}>
									<FormControl
										label='nczi.electronicDPN.create.outings.timeTo'
										name='timeTo'
										isRequired>
										<TimePicker />
									</FormControl>
								</div>
								<div className={'col-2'}>
									<div className={styles.removeItemWrapper}>
										<RemoveButton
											onClick={() => {
												remove(index);
											}}
										/>
									</div>
								</div>
							</div>
						</FormContainer>
					)}
				</FormMultiplier>
				<FormTitle
					tx='nczi.electronicDPN.create.insuranceExemption.title'
					type='default'
				/>
				{exemptionedItems.length > 0 && (
					<div>
						<Text
							tx={'nczi.electronicDPN.create.insuranceExemption.totalItems'}
							inline
						/>
						: {exemptionedItems.length}
					</div>
				)}
				<LinkButton
					firstUpper
					title={'nczi.electronicDPN.create.insuranceExemption.add'}
					theme={Theme.BLUE}
					onClick={() => setInsuranceExemptionListModalOpen(true)}
				/>
				<div className='mt-4'>
					<SubmitButton
						title='component.nczi.setup.save'
						onSubmit={handleSubmit}
					/>
				</div>
			</FormWrapper>
			<InsuranceExemptionListModal
				isOpen={insuranceExemptionListModalOpen}
				onClose={() => setInsuranceExemptionListModalOpen(false)}
				onSubmit={handleInsuranceExemption}
				patientIdentifier={patientIdentifier}
				initialExemptionedItems={exemptionedItems}
			/>
		</div>
	);
};

export default withForm(ElectronicDPNCreateForm);
